// Marco del panel: el mismo menú lateral de la aplicación del negocio, con las mismas clases del sistema de
// diseño, para que el equipo no tenga que aprender otra forma de moverse. Lo que cambia es lo que hay adentro.
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Icon, type NombreIcono } from '@glubi/ds';
import logotipo from '@glubi/ds/assets/glubi-logotipo-inverso.svg';
import { api } from '../lib/api';
import { useSesion } from '../lib/sesion';

const TITULO: Record<string, string> = {
  '/cuentas': 'Cuentas',
  '/riesgos': 'Programa de validación',
  '/ingesta': 'Ingesta y conectores',
  '/certificacion': 'Certificación',
  '/cobro': 'Cobro',
  '/costo': 'Costo de servir',
  '/creditos': 'Créditos del constructor',
  '/modelos': 'Modelos de Glubi',
};

export function Marco({ children }: { children: ReactNode }) {
  const { yo, salir } = useSesion();
  const [menu, setMenu] = useState(false);
  const ruta = useRouterState({ select: (s) => s.location.pathname });

  // Los dos números que hacen que alguien entre al panel: negocios que se apagan y versiones esperando revisión.
  const panel = useQuery({ queryKey: ['panel'], queryFn: () => api<any>('/api/panel'), refetchInterval: 120_000 });
  const certificacion = useQuery({ queryKey: ['certificacion'], queryFn: () => api<any[]>('/api/certificacion'), refetchInterval: 300_000 });
  const apagandose = panel.data?.resumen?.apagandose ?? 0;
  const porCertificar = certificacion.data?.length ?? 0;

  useEffect(() => setMenu(false), [ruta]);
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  const item = (to: string, label: string, icon: NombreIcono, extra?: { count?: number; alerta?: boolean }) => {
    const activo = ruta === to;
    return (
      <Link key={to} to={to} className={`nav-item ${activo ? 'active' : ''} ${extra?.alerta ? 'quarantine' : ''}`} aria-current={activo ? 'page' : undefined} style={{ textDecoration: 'none' }}>
        <Icon name={icon} size={16} color={extra?.alerta ? 'var(--amber)' : 'var(--paper)'} />
        {label}
        {Boolean(extra?.count) && <span className="nav-count">{extra!.count}</span>}
      </Link>
    );
  };

  return (
    <div className="shell">
      <header className="topbar">
        <button className="menu-btn" aria-label="Abrir el menú" aria-expanded={menu} aria-controls="menu-panel" onClick={() => setMenu(true)}>
          <Icon name="menu" size={20} />
        </button>
        <img src={logotipo} alt="Glubi" />
        <span className="where">{TITULO[ruta] ?? 'Panel del equipo'}</span>
      </header>
      <div className={`drawer-scrim ${menu ? 'open' : ''}`} onClick={() => setMenu(false)} />
      <nav id="menu-panel" className={`sidebar ${menu ? 'open' : ''}`} aria-label="Navegación del panel">
        <img src={logotipo} alt="Glubi" className="sidebar-logo" />
        <div className="nav-eyebrow" style={{ marginTop: 0 }}>Panel del equipo</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {item('/cuentas', 'Cuentas', 'users', { count: apagandose, alerta: apagandose > 0 })}
          {item('/riesgos', 'Programa de validación', 'flask-conical')}
        </div>
        <div className="nav-eyebrow">Operación</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {item('/ingesta', 'Ingesta y conectores', 'signal')}
          {item('/certificacion', 'Certificación', 'shield', { count: porCertificar, alerta: porCertificar > 0 })}
        </div>
        <div className="nav-eyebrow">Negocio</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {item('/cobro', 'Cobro', 'receipt')}
          {item('/costo', 'Costo de servir', 'scale')}
          {item('/creditos', 'Créditos del constructor', 'message-square')}
          {item('/modelos', 'Modelos de Glubi', 'bot')}
        </div>
        <div style={{ marginTop: 'auto', paddingTop: 20 }}>
          <div className="biz-switch">
            <div className="biz-option" style={{ cursor: 'default', opacity: 0.6 }}>
              {yo?.usuario.correo}
              <small>Equipo Glubi</small>
            </div>
            <button className="biz-option" onClick={() => void salir()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="log-out" size={12} /> Salir
            </button>
          </div>
        </div>
      </nav>
      <main className="main">{children}</main>
    </div>
  );
}
